
import { Button, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles';
import React, { useState, useEffect, MouseEventHandler } from 'react'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import usePreviousValue from 'smartstream-widgets/hooks/usePreviousValue/usePreviousValue'
import { getFormattedPrice } from 'smartstream-widgets/components/Price/Price'
import GSSVG from 'smartstream-widgets/controls/GSSVG'
import { isValidURL } from 'widgets-shared/utilities'

import { PriceFormat } from 'smartstream-widgets/components/Price/types'
import { MessageBusType } from 'smartstream-widgets/types/sharedModule/core'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { MarketSelectionType } from '../../types/market'
import { getMeasurablePrice } from 'smartstream-widgets/utils/getMeasurablePrice'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { getMarketHandicap, getMarketOutcome } from '../MarketSelectionButton/utils/getMarketLabel';

const styles = (theme: Theme) => createStyles({
  arrow: {
    color: 'transparent',
    position: 'absolute',
    left: 0,
    width: 12,
    height: 20
  },
  muiButtonLabel: {
    display: 'inherit',
    alignItems: 'inherit',
    justifyContent: 'inherit'
  },
  buttonBase: {
    color: theme.palette.primary.main,
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.action.active
      }
    },
    '&:hover': {
      background: theme.palette.primary.main,
      '& $captionOdd': {
        color: theme.palette.primary.contrastText
      },      
      '& $openInNewIcon':{
        color: theme.palette.common.white
      }
    },
  },
  buttonDisabled: {},
  buttonDown: {
    color: theme.palette.error.main,
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.error.main
      }
    },
    '&:hover': {
      background: theme.palette.error.main,
      '& $captionOdd': {
        color: theme.palette.common.white
      },
      '& $arrow': {
        color: theme.palette.common.white
      },
      '& $openInNewIcon':{
        color: theme.palette.common.white
      }
    },
    '& $arrow': {
      color: theme.palette.error.main,
      transform: 'scaleY(-1)'
    }
  },
  buttonLabel: {
    flexDirection: 'column',
    width: 'auto',
    position: 'relative',
    '& $captionOdd': {
      fontWeight: 'bold'
    }
  },
  buttonUp: {
    color: theme.palette.success.main,
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.success.main
      }
    },
    '&:hover': {
      background: theme.palette.success.main,
      '& $captionOdd': {
        color: theme.palette.common.white
      },
      '& $arrow': {
        color: theme.palette.common.white
      },
      '& $openInNewIcon':{
        color: theme.palette.common.white
      }
    },
    '& $arrow': {
      color: theme.palette.success.main
    }
  },
  captionOdd: {
    color: 'inherit',
    fontSize: 14,
    lineHeight: 1.2,
    padding: `0px 11px`,
    transition: 'color .3s ease-in',
    '&:nth-last-child(3)': {
      fontSize: 8,
      fontWeight: 'normal'
    }
  },
  oddButton: {
    transform: 'rotate(0deg)',
    background: alpha(theme.palette.background.paper, 0),
    border: '1px solid currentColor',
    boxShadow: 'none',
    display: 'inline-flex',
    minHeight: 33,
    minWidth: 72,
    padding: `0px 3px`,
    '&$buttonDisabled': {
      border: 'none',
      backgroundColor: 'transparent',
      color: theme.palette.action.active
    }
  },
  root: {
    position: 'relative'
  },
  selectionLabel: {
    left: 0,
    fontFamily: `${theme.typography['variantFontFamily']},${theme.typography['fontFamily']}`,
    fontSize: 10,
    fontWeight: 'bold',
    position: 'absolute',
    textAlign: 'center',
    top: -17,
    width: '100%'
  },
  openInNewIcon:{
    position: 'fixed',
    right: 2,
    top: 2,
    fontSize: 10,
    display: 'none'
  },
  buttonSelected: {}
})

type Props = {
  animationKey?: string,
  classes: {
    [K in string]: string
  },
  config: {
    BASE_ASSET_PATH: string,
    bookmakers: {
      [K in string]: {
        linkTemplate: string
      }
    },
    defaultLink: string,
    marketSelection: {
      action: 'none' | 'redirect' | 'post' | 'deeplink' | 'select',
      priceUpdateDuration: number,
      target: string
    },
    oddsFormat: 'american' | 'decimal' | 'fraction'
  },
  messageBus: MessageBusType,
  label: string,
  oddsFormat: 'american' | 'decimal' | 'fraction',
  selection: MarketSelectionType,
  buttonVariant?: 'text' | 'contained' | 'outlined',
  disabled?: boolean,
  selected?: boolean
}

export function validateShouldSetTrend(prevPrice, price, setTrend) {
  if (price && prevPrice) {
    price !== prevPrice && setTrend(price > prevPrice)
  }
}

export const MarketSelection = (props: Props) => {
  const {
    animationKey = 'key',
    classes,
    config: {
      BASE_ASSET_PATH,
      marketSelection: {
        action = 'redirect',
        priceUpdateDuration,
        target = '_blank'
      } = {},
      oddsFormat: defaultOddsFormat = 'american'
    },
    messageBus,
    label = '',
    oddsFormat,
    selection,
    buttonVariant= 'contained',
    disabled = false,
    selected = false
  } = props

  const { price, link } = selection || {}
  const href = isValidURL(link) ? link : null
  const measurablePrice = getMeasurablePrice(price)
  const prevPrice: PriceFormat | number | null = usePreviousValue(measurablePrice)
  const prevAnimationKey: string | null = usePreviousValue(animationKey)
  const [trend, setTrend] = useState<boolean | null | undefined>(undefined)
  useEffect(() => {
    let timeoutId: any
    if (prevPrice && measurablePrice &&
      prevAnimationKey === animationKey
    ) {
      validateShouldSetTrend(prevPrice, measurablePrice, setTrend)
      timeoutId = setTimeout(() => {
        setTrend(undefined)
      }, priceUpdateDuration || 20000)
    }
    return () => {
      clearTimeout(timeoutId)
      setTrend(undefined)
    }
  }, [price])

  const arrowIcon = `${BASE_ASSET_PATH}/img/icons.svg#ic-price-change-arrow`
  const marketHandicap = getMarketHandicap(selection)
  const marketOutcome = getMarketOutcome(selection)
  const formattedPrice = getFormattedPrice(oddsFormat || defaultOddsFormat, price)
  const clickHandler: MouseEventHandler = (event) => {
    if (action !== 'redirect') event.preventDefault()
    const selectionClickEvent = {
      action,
      event,
      redirectUrl: href,
      selection
    }
    messageBus && messageBus.dispatchEvent('market-selection:click', selectionClickEvent)
  }

  return (
    <div className={classes.root} data-testid="market-selection-container">
      <Typography className={classes.selectionLabel} color='primary'>{label}</Typography>
      <Button
        data-testid='market-selection'
        data-ht-category='market-selection'
        href={href}
        classes={{
          root: `${classes.oddButton} ${trend === undefined ? classes.buttonBase : trend ? classes.buttonUp : classes.buttonDown} ${selected && classes.buttonSelected}`,
          disabled: classes.buttonDisabled
        }}
        color='primary'
        disabled={disabled || action === 'none' || !href}
        onClick={action !== 'none' ? clickHandler : undefined}
        target={target}
        variant={buttonVariant}
      >
        <span className={`${classes.buttonLabel} ${classes.muiButtonLabel}`}>
          {
            (marketHandicap || marketOutcome) &&
            <Typography className={classes.captionOdd}>
              {marketOutcome || ''} {marketHandicap || ''}
            </Typography>
          }
          <Typography className={classes.captionOdd}>
            {formattedPrice}
          </Typography>
          <GSSVG
            className={classes.arrow}
            xlinkHref={arrowIcon}
          />
          {href &&
            <OpenInNew
              className={classes.openInNewIcon}
            />
          }
        </span>
      </Button>
    </div>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'MarketSelection', defaultTheme: getDefaultTheme })
)(MarketSelection)
